import { useContext, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { AuthContext } from '../../../contexts/AuthContext';
import { addComment } from '../../../database/profileComments';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';

const MIN_INTERVAL_MS = 5000;
const MAX_LENGTH = 1000;

const formatMuteUntil = (ts) => {
  if (!ts) return null;
  const ms = ts.toMillis?.() ?? (ts.seconds ? ts.seconds * 1000 : null);
  if (!ms) return null;
  return new Date(ms).toLocaleString();
};

const ComposeBox = ({ ownerUserId }) => {
  const { currentUser, userProfile, isCommentBanned, isSiteBanned, muteActiveUntil } =
    useContext(AuthContext);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const lastPostedAt = useRef(0);

  if (!currentUser) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground">
        Sign in to leave a comment.
      </div>
    );
  }

  if (isSiteBanned) return null;

  const muteUntilLabel = formatMuteUntil(muteActiveUntil);
  const banned = isCommentBanned;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (banned) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    const now = Date.now();
    if (now - lastPostedAt.current < MIN_INTERVAL_MS) {
      toast.error('Slow down — wait a few seconds before posting again.');
      return;
    }

    setSubmitting(true);
    try {
      await addComment({
        ownerUserId,
        author: {
          uid: currentUser.uid,
          username: userProfile?.username || currentUser.email || 'Player',
          photoURL: userProfile?.profilePicUrl || null,
        },
        text: trimmed,
      });
      lastPostedAt.current = now;
      setText('');
    } catch (err) {
      toast.error('Could not post comment.', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (banned) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        You're currently{' '}
        {muteUntilLabel ? `muted until ${muteUntilLabel}` : 'banned from commenting'}.
      </div>
    );
  }

  const remaining = MAX_LENGTH - text.length;

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
        placeholder="Leave a comment…"
        disabled={submitting}
      />
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {remaining} chars left
        </span>
        <Button type="submit" size="sm" disabled={submitting || !text.trim()}>
          <Send className="h-3.5 w-3.5" />
          {submitting ? 'Posting…' : 'Post'}
        </Button>
      </div>
    </form>
  );
};

export default ComposeBox;
